import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { categories, initialProducts } from '../../mocks/products';

interface Product {
  id: string;
  name: string;
  category: string;
  price: number;
  image: string;
  sizes: string[];
  available: boolean;
  description: string;
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [imageUploadMode, setImageUploadMode] = useState<'url' | 'file'>('url');
  const [uploadedImage, setUploadedImage] = useState<string>('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const ADMIN_PASSWORD = 'mignonfally2025';

  useEffect(() => {
    const auth = sessionStorage.getItem('admin_auth');
    if (auth === 'true') {
      setIsAuthenticated(true);
      loadProducts();
    }
  }, []);

  const loadProducts = () => {
    try {
      const stored = localStorage.getItem('mignonfally_products');
      if (stored) {
        setProducts(JSON.parse(stored));
      } else {
        setProducts(initialProducts);
        try {
          localStorage.setItem('mignonfally_products', JSON.stringify(initialProducts));
        } catch (e) {
          console.warn('LocalStorage quota exceeded');
        }
      }
    } catch (error) {
      console.error('Error loading products:', error);
      setProducts(initialProducts);
    }
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('admin_auth', 'true');
      loadProducts();
    } else {
      alert('Mot de passe incorrect');
    }
  };

  const handleLogout = () => {
    if (hasUnsavedChanges) {
      if (!confirm('Vous avez des modifications non enregistrées. Voulez-vous vraiment vous déconnecter ?')) {
        return;
      }
    }
    setIsAuthenticated(false);
    sessionStorage.removeItem('admin_auth');
    setPassword('');
    setHasUnsavedChanges(false);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Vérifier la taille du fichier (max 500KB)
      if (file.size > 500000) {
        alert('L\'image est trop volumineuse. Veuillez utiliser une image de moins de 500KB ou utiliser une URL d\'image.');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedImage(reader.result as string);
        if (editingProduct) {
          setEditingProduct({ ...editingProduct, image: reader.result as string });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const saveProducts = (updatedProducts: Product[]) => {
    try {
      localStorage.setItem('mignonfally_products', JSON.stringify(updatedProducts));
      setProducts(updatedProducts);
      setHasUnsavedChanges(false);
    } catch (e) {
      if (e instanceof DOMException && e.name === 'QuotaExceededError') {
        alert('Erreur : Espace de stockage insuffisant. Veuillez utiliser des URLs d\'images au lieu de télécharger des fichiers volumineux.');
      } else {
        alert('Erreur lors de la sauvegarde des produits');
      }
      console.error('Error saving products:', e);
    }
  };

  const handleSaveProduct = () => {
    if (!editingProduct) return;

    const updatedProducts = products.map(p => 
      p.id === editingProduct.id ? editingProduct : p
    );
    setProducts(updatedProducts);
    setHasUnsavedChanges(true);
    setEditingProduct(null);
    setUploadedImage('');
    alert('Modification enregistrée. N\'oubliez pas de cliquer sur "Enregistrer toutes les modifications" pour valider.');
  };

  const handleAddProduct = () => {
    if (!editingProduct) return;

    const newProduct = {
      ...editingProduct,
      id: Date.now().toString()
    };
    setProducts([...products, newProduct]);
    setHasUnsavedChanges(true);
    setShowAddModal(false);
    setEditingProduct(null);
    setUploadedImage('');
    alert('Produit ajouté. N\'oubliez pas de cliquer sur "Enregistrer toutes les modifications" pour valider.');
  };

  const handleDeleteProduct = (id: string) => {
    if (confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) {
      const updatedProducts = products.filter(p => p.id !== id);
      setProducts(updatedProducts);
      setHasUnsavedChanges(true);
      alert('Produit supprimé. N\'oubliez pas de cliquer sur "Enregistrer toutes les modifications" pour valider.');
    }
  };

  const toggleAvailability = (id: string) => {
    const updatedProducts = products.map(p =>
      p.id === id ? { ...p, available: !p.available } : p
    );
    setProducts(updatedProducts);
    setHasUnsavedChanges(true);
  };

  const handleSaveAllChanges = () => {
    if (!hasUnsavedChanges) {
      alert('Aucune modification à enregistrer');
      return;
    }
    
    if (confirm('Voulez-vous enregistrer toutes les modifications et mettre à jour le site ?')) {
      saveProducts(products);
      alert('✅ Toutes les modifications ont été enregistrées avec succès ! Le site est maintenant à jour.');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-r from-amber-600 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-lock-line text-4xl text-white"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-800" style={{ fontFamily: 'Playfair Display, serif' }}>
              Administration
            </h2>
            <p className="text-gray-600 mt-2">Accès réservé aux administrateurs</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                Mot de passe
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none"
                placeholder="Entrez le mot de passe"
                required
              />
            </div>
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-amber-600 to-orange-600 text-white py-3 rounded-lg font-semibold hover:shadow-xl transition-all whitespace-nowrap cursor-pointer"
            >
              Se connecter
            </button>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-all whitespace-nowrap cursor-pointer"
            >
              Retour à l'accueil
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      {/* Navigation */}
      <nav className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-3">
              <img 
                src="https://static.readdy.ai/image/f9195bf5ed7777e5d4f9639b66c05e87/d185bafd3eb9956aa2cc41f5ce977164.png" 
                alt="MignonFally_Store Logo" 
                className="h-12 w-12 object-contain"
              />
              <h1 className="text-2xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent" style={{ fontFamily: 'Playfair Display, serif' }}>
                Administration
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/')}
                className="text-gray-700 hover:text-amber-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-home-line text-xl mr-2"></i>
                Accueil
              </button>
              <button 
                onClick={handleLogout}
                className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-logout-box-line mr-2"></i>
                Déconnexion
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Admin Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-800 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Gestion des produits
            </h2>
            {hasUnsavedChanges && (
              <p className="text-orange-600 font-semibold flex items-center gap-2">
                <i className="ri-alert-line"></i>
                Vous avez des modifications non enregistrées
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleSaveAllChanges}
              className={`px-6 py-3 rounded-lg font-bold flex items-center gap-2 whitespace-nowrap cursor-pointer transition-all ${
                hasUnsavedChanges
                  ? 'bg-green-600 text-white hover:bg-green-700 hover:shadow-xl animate-pulse'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              disabled={!hasUnsavedChanges}
            >
              <i className="ri-save-line text-xl"></i>
              Enregistrer toutes les modifications
            </button>
            <button
              onClick={() => {
                setShowAddModal(true);
                setEditingProduct({
                  id: '',
                  name: '',
                  category: categories[0].id,
                  price: 0,
                  image: '',
                  sizes: ['S', 'M', 'L', 'XL'],
                  available: true,
                  description: ''
                });
              }}
              className="bg-gradient-to-r from-amber-600 to-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-xl transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-add-line text-xl"></i>
              Ajouter un produit
            </button>
          </div>
        </div>

        {/* Products Table */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-amber-600 to-orange-600 text-white">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Image</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Nom</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Catégorie</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Prix</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Tailles</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Statut</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {products.map(product => (
                  <tr key={product.id} className="hover:bg-amber-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden">
                        <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                      </div>
                    </td>
                    <td className="px-6 py-4 font-medium text-gray-800">{product.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {categories.find(c => c.id === product.category)?.name}
                    </td>
                    <td className="px-6 py-4 font-semibold text-amber-600">
                      {product.price.toLocaleString()} FCFA
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {product.sizes.join(', ')}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => toggleAvailability(product.id)}
                        className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer ${
                          product.available
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {product.available ? 'Disponible' : 'Indisponible'}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setEditingProduct(product)}
                          className="w-8 h-8 flex items-center justify-center bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors cursor-pointer"
                          title="Modifier"
                        >
                          <i className="ri-edit-line"></i>
                        </button>
                        <button
                          onClick={() => handleDeleteProduct(product.id)}
                          className="w-8 h-8 flex items-center justify-center bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors cursor-pointer"
                          title="Supprimer"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Edit/Add Modal */}
      {(editingProduct || showAddModal) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-8">
            <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <h3 className="text-xl font-bold">
                {showAddModal ? 'Ajouter un produit' : 'Modifier le produit'}
              </h3>
              <button
                onClick={() => {
                  setEditingProduct(null);
                  setShowAddModal(false);
                  setUploadedImage('');
                }}
                className="w-8 h-8 flex items-center justify-center hover:bg-white/20 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <div className="p-6 space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Nom du produit</label>
                <input
                  type="text"
                  value={editingProduct?.name || ''}
                  onChange={(e) => setEditingProduct(editingProduct ? {...editingProduct, name: e.target.value} : null)}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Catégorie</label>
                <select
                  value={editingProduct?.category || ''}
                  onChange={(e) => setEditingProduct(editingProduct ? {...editingProduct, category: e.target.value} : null)}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none text-sm"
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Prix (FCFA)</label>
                <input
                  type="number"
                  value={editingProduct?.price || 0}
                  onChange={(e) => setEditingProduct(editingProduct ? {...editingProduct, price: Number(e.target.value)} : null)}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                <textarea
                  value={editingProduct?.description || ''}
                  onChange={(e) => setEditingProduct(editingProduct ? {...editingProduct, description: e.target.value} : null)}
                  rows={3}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none text-sm resize-none"
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Image</label>
                <div className="flex gap-2 mb-3">
                  <button
                    onClick={() => setImageUploadMode('url')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                      imageUploadMode === 'url'
                        ? 'bg-amber-600 text-white'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    URL
                  </button>
                  <button
                    onClick={() => setImageUploadMode('file')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                      imageUploadMode === 'file'
                        ? 'bg-amber-600 text-white'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    Télécharger
                  </button>
                </div>

                {imageUploadMode === 'url' ? (
                  <input
                    type="text"
                    value={editingProduct?.image || ''}
                    onChange={(e) => setEditingProduct(editingProduct ? {...editingProduct, image: e.target.value} : null)}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none text-sm"
                    placeholder="URL de l'image"
                  />
                ) : (
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none text-sm"
                    />
                    {uploadedImage && (
                      <div className="mt-3 w-32 h-32 bg-gray-100 rounded-lg overflow-hidden">
                        <img src={uploadedImage} alt="Preview" className="w-full h-full object-cover" />
                      </div>
                    )}
                  </div>
                )}

                {editingProduct?.image && (
                  <div className="mt-3 w-32 h-32 bg-gray-100 rounded-lg overflow-hidden">
                    <img src={editingProduct.image} alt="Current" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Tailles (séparées par des virgules)</label>
                <input
                  type="text"
                  value={editingProduct?.sizes.join(', ') || ''}
                  onChange={(e) => setEditingProduct(editingProduct ? {...editingProduct, sizes: e.target.value.split(',').map(s => s.trim())} : null)}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none text-sm"
                  placeholder="S, M, L, XL"
                />
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="available"
                  checked={editingProduct?.available || false}
                  onChange={(e) => setEditingProduct(editingProduct ? {...editingProduct, available: e.target.checked} : null)}
                  className="w-5 h-5 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                />
                <label htmlFor="available" className="text-sm font-semibold text-gray-700 cursor-pointer">
                  Produit disponible
                </label>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 rounded-b-2xl flex gap-3">
              <button
                onClick={() => {
                  setEditingProduct(null);
                  setShowAddModal(false);
                  setUploadedImage('');
                }}
                className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-all whitespace-nowrap cursor-pointer"
              >
                Annuler
              </button>
              <button
                onClick={showAddModal ? handleAddProduct : handleSaveProduct}
                className="flex-1 bg-gradient-to-r from-amber-600 to-orange-600 text-white py-3 rounded-lg font-semibold hover:shadow-xl transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-save-line mr-2"></i>
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
